import { ApiResponseError } from '@/api/ApiResponseError';
import { UnauthenticatedError } from '@/api/UnauthenticatedError';

import auth0 from '@/auth/auth0Plugin';
import { getActivePinia } from 'pinia';

async function getAgencyAuthorizationParams(): Promise<{
  selectedCustomerId: string | undefined;
  selectedPersonId: string | undefined;
}> {
  const pinia = getActivePinia();
  if (!pinia) {
    return { selectedCustomerId: undefined, selectedPersonId: undefined };
  }

  // eslint-disable-next-line import/dynamic-import-chunkname
  const { useAgencyStore } = await import('@/stores/agency/useAgencyStore');
  const agencyStore = useAgencyStore(pinia);
  return {
    selectedCustomerId: agencyStore.activeAgencyId,
    selectedPersonId: agencyStore.activeAgencyPersonId,
  };
}

interface CreateRequest {
  method: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  credentials?: RequestCredentials;
  headers?: HeadersInit;
}

const getResponseErrorCode = async (response: Response): Promise<string | undefined> => {
  try {
    const body: unknown = await response.clone().json();
    if (typeof body !== 'object' || body === null) return undefined;
    const record = body as Record<string, unknown>;
    const nested =
      typeof record.error === 'object' && record.error !== null
        ? (record.error as Record<string, unknown>)
        : undefined;
    return [record.code, record.errorCode, record.error, nested?.code].find(
      (value): value is string => typeof value === 'string',
    );
  } catch {
    // Error bodies are optional; status handling still applies.
  }
  return undefined;
};

export function createRequest({ method, credentials = 'include', headers = {} }: CreateRequest) {
  return async (endpoint: string, body?: string, additionalHeaders = {}): Promise<Response> => {
    const authToken = await auth0.getAccessTokenSilently({
      cacheMode: 'on',
      authorizationParams: await getAgencyAuthorizationParams(),
    });

    const response: Response = await fetch(endpoint, {
      method,
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        token: authToken as string,
        // some services use Authorization instead of the token naming
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...headers,
        ...additionalHeaders,
      },
      credentials,
      body,
    });

    if (response.ok) {
      return response;
    }

    if (response.status === 403 || response.status === 401) {
      throw new UnauthenticatedError(
        `The user is not authenticated: ${response.status}`,
        response.status,
      );
    }

    let message = `API endpoint ${endpoint} responded with an error code: ${response.status}`;
    if (response.statusText) {
      message += `. Error=${response.statusText}`;
    }
    throw new ApiResponseError(message, response.status, await getResponseErrorCode(response));
  };
}

export const get = createRequest({ method: 'GET' });
export const post = createRequest({ method: 'POST' });
export const put = createRequest({ method: 'PUT' });
export const deleteRequest = createRequest({ method: 'DELETE' });
export const patch = createRequest({ method: 'PATCH' });
